/**
 * Widget F: FEGLI Coverage Reduction Risk
 * 
 * Displays a vertical bar chart showing the distribution of clients with FEGLI coverage
 * by reduction risk level: Low (<25%), Moderate (25-50%), High (>50%).
 */

import React, { useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "../../ui/card";
import { ClientType } from "../../../types";
import { calculateFEGLIRisk, FEGLIRiskData } from "../utils/widgetCalculations";
import { calculateFEGLIReduction } from "../utils/calculations";
import { UNIFIED_CHART_COLORS, SEMANTIC_PASTELS, ACCENT_COLOR } from "./widgetColors";
import { ChartTooltip, BAR_CHART_MARGINS, AXIS_STYLE, GRID_STYLE, CHART_ANIMATION } from "./chartUtils";
import XanoAPI from "../../../lib/XanoAPI";
import { isDataEmpty, getSampleData } from "../utils/sampleData";
import { Badge } from "../../ui/badge";

interface FEGLIRiskWidgetProps {
  clients: ClientType[];
  isLoading?: boolean;
  userId?: number; // Optional user ID filter (singular int)
}

const RISK_COLORS = {
  low: SEMANTIC_PASTELS.success,    // Mint green (pastel)
  moderate: SEMANTIC_PASTELS.warning, // Lemon yellow (pastel)
  high: SEMANTIC_PASTELS.error,      // Coral (pastel)
};

const RISK_LABELS = {
  low: "Low (<25%)",
  moderate: "Moderate (25-50%)",
  high: "High (>50%)",
};

export function FEGLIRiskWidget({
  clients,
  isLoading = false,
  userId,
}: FEGLIRiskWidgetProps) {
  const api = XanoAPI.getInstance();

  const { data: apiData, isLoading: apiLoading, error: apiError } = useQuery({
    queryKey: ["fegli-risk", userId],
    queryFn: async () => {
      const result = await api.getFEGLIRisk(userId);
      console.log("🔍 FEGLIRiskWidget: API response:", result);
      console.log("🔍 FEGLIRiskWidget: API response type:", typeof result);
      console.log("🔍 FEGLIRiskWidget: Has categories?", !!result?.categories);
      if (result?.categories) {
        console.log("🔍 FEGLIRiskWidget: Categories:", JSON.stringify(result.categories, null, 2));
      }
      return result;
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Log any errors
  useEffect(() => {
    if (apiError) {
      console.error("❌ FEGLIRiskWidget: Query error:", apiError);
    }
  }, [apiError]);

  const data = useMemo(() => {
    let result: {
      categories: {
        low: number;
        moderate: number;
        high: number;
      };
    };
    
    console.log("🔍 FEGLIRiskWidget: Processing data - apiData:", apiData);
    console.log("🔍 FEGLIRiskWidget: apiData is null?", apiData === null);
    console.log("🔍 FEGLIRiskWidget: apiData is undefined?", apiData === undefined);
    console.log("🔍 FEGLIRiskWidget: Clients available:", clients.length);
    
    // Debug: Check how many clients have FEGLI coverage
    if (clients.length > 0) {
      // Helper to check if a value is truthy (handles boolean, string "true", 1, etc.)
      const isTruthy = (value: any): boolean => {
        if (value === true || value === 1 || value === "1") return true;
        if (typeof value === 'string' && value.toLowerCase() === 'true') return true;
        return false;
      };
      
      const clientsWithFEGLI = clients.filter(c => {
        const hasBasic = isTruthy(c.fegli_basicCoverage);
        const hasOptionA = isTruthy(c.fegli_optionA);
        const hasOptionB = isTruthy(c.fegli_optionB);
        const hasOptionC = isTruthy(c.fegli_optionC);
        return hasBasic || hasOptionA || hasOptionB || hasOptionC;
      });
      console.log(`🔍 FEGLIRiskWidget: Clients with FEGLI coverage (raw check): ${clientsWithFEGLI.length} out of ${clients.length}`);
      
      // Debug: Log sample clients to check FEGLI data structure
      const sampleClients = clients.slice(0, Math.min(5, clients.length));
      const sampleData = sampleClients.map(c => {
        const clientAny = c as any;
        return {
          id: c.id,
          name: `${c.firstName || ''} ${c.lastName || ''}`.trim() || `Client ${c.id}`,
          fegli_basicCoverage: c.fegli_basicCoverage,
          fegli_optionA: c.fegli_optionA,
          fegli_optionB: c.fegli_optionB,
          fegli_optionC: c.fegli_optionC,
          fegli_optionBAge65Reduction: clientAny.fegli_optionBAge65Reduction,
          fegli_optionCAge65Reduction: clientAny.fegli_optionCAge65Reduction,
          fegli_reductionAtAge65: clientAny.fegli_reductionAtAge65,
          fegliReductionPercentage: clientAny.fegliReductionPercentage,
          rawTypes: {
            basic: typeof c.fegli_basicCoverage,
            optionA: typeof c.fegli_optionA,
            optionB: typeof c.fegli_optionB,
            optionC: typeof c.fegli_optionC,
          },
          rawValues: {
            basic: c.fegli_basicCoverage,
            optionA: c.fegli_optionA,
            optionB: c.fegli_optionB,
            optionC: c.fegli_optionC,
          },
          isTruthyCheck: {
            basic: isTruthy(c.fegli_basicCoverage),
            optionA: isTruthy(c.fegli_optionA),
            optionB: isTruthy(c.fegli_optionB),
            optionC: isTruthy(c.fegli_optionC),
          },
          // Check for alternative field names
          allFEGLIProperties: Object.keys(clientAny).filter(key => 
            key.toLowerCase().includes('fegli')
          ).reduce((acc: any, key) => {
            acc[key] = {
              value: clientAny[key],
              type: typeof clientAny[key],
              isTruthy: isTruthy(clientAny[key]),
            };
            return acc;
          }, {}),
        };
      });
      console.log("🔍 FEGLIRiskWidget: Sample clients FEGLI data:", sampleData);
      
      // Debug: Check if ANY clients have FEGLI-related fields with ANY truthy value
      const allFEGLIFields = clients.map(c => {
        const clientAny = c as any;
        return {
          id: c.id,
          hasAnyFEGLI: !!(
            clientAny.fegli_basicCoverage ||
            clientAny.fegli_optionA ||
            clientAny.fegli_optionB ||
            clientAny.fegli_optionC ||
            clientAny.fegli ||
            clientAny.FEGLI ||
            clientAny.fegli_basic ||
            clientAny.fegli_option_a ||
            clientAny.fegli_option_b ||
            clientAny.fegli_option_c
          ),
          allFEGLIKeys: Object.keys(clientAny).filter(key => 
            key.toLowerCase().includes('fegli')
          ),
        };
      });
      const clientsWithAnyFEGLI = allFEGLIFields.filter(c => c.hasAnyFEGLI || c.allFEGLIKeys.length > 0);
      console.log(`🔍 FEGLIRiskWidget: Clients with ANY FEGLI-related fields: ${clientsWithAnyFEGLI.length}`);
      if (clientsWithAnyFEGLI.length > 0) {
        const sampleClientWithKeys = clientsWithAnyFEGLI[0];
        const sampleClientObj = clients.find(c => c.id === sampleClientWithKeys.id);
        if (sampleClientObj) {
          const clientAny = sampleClientObj as any;
          const fegliValues: any = {};
          sampleClientWithKeys.allFEGLIKeys.forEach(key => {
            fegliValues[key] = {
              value: clientAny[key],
              type: typeof clientAny[key],
              isTruthy: isTruthy(clientAny[key]),
            };
          });
          console.log("🔍 FEGLIRiskWidget: Sample client FEGLI field details:", {
            id: sampleClientObj.id,
            name: `${sampleClientObj.firstName || ''} ${sampleClientObj.lastName || ''}`.trim() || `Client ${sampleClientObj.id}`,
            fegliFields: fegliValues,
            allFEGLIKeys: sampleClientWithKeys.allFEGLIKeys,
          });
          
          // Explicitly log each FEGLI field for debugging
          console.log("🔍 FEGLIRiskWidget: Detailed FEGLI field breakdown:", JSON.stringify(fegliValues, null, 2));
          
          // Check specifically for the 10 clients with FEGLI data
          const clientsWithFEGLIData = clients.filter(c => {
            const cAny = c as any;
            // Check all possible FEGLI coverage indicators
            return isTruthy(cAny.fegli_basicCoverage) || 
                   isTruthy(cAny.fegli_optionA) || 
                   isTruthy(cAny.fegli_optionB) || 
                   isTruthy(cAny.fegli_optionC) ||
                   // Also check if any FEGLI field has a truthy value
                   Object.keys(cAny).some(key => 
                     key.toLowerCase().includes('fegli') && 
                     (isTruthy(cAny[key]) || (typeof cAny[key] === 'string' && cAny[key].trim() !== ''))
                   );
          });
          console.log(`🔍 FEGLIRiskWidget: Clients with ANY truthy FEGLI data: ${clientsWithFEGLIData.length}`);
          if (clientsWithFEGLIData.length > 0) {
            console.log("🔍 FEGLIRiskWidget: First client with FEGLI data:", {
              id: clientsWithFEGLIData[0].id,
              name: `${clientsWithFEGLIData[0].firstName || ''} ${clientsWithFEGLIData[0].lastName || ''}`.trim(),
              allFEGLIProps: Object.keys(clientsWithFEGLIData[0] as any).filter(k => k.toLowerCase().includes('fegli')).reduce((acc: any, k) => {
                acc[k] = (clientsWithFEGLIData[0] as any)[k];
                return acc;
              }, {}),
            });
          }
        }
      }
    }
    
    let usingSampleData = false;
    
    // Use API data if it exists and has categories structure with non-zero values
    if (apiData && apiData.categories && typeof apiData.categories === 'object' && Object.keys(apiData.categories).length > 0) {
      const totalClients = Object.values(apiData.categories).reduce((sum: number, val: any) => sum + (typeof val === 'number' ? val : 0), 0);
      console.log("🔍 FEGLIRiskWidget: API data found - Total clients:", totalClients);
      console.log("🔍 FEGLIRiskWidget: Categories structure:", JSON.stringify(apiData.categories, null, 2));
      
      if (totalClients > 0) {
        // API data has values - use it
        console.log("✅ FEGLIRiskWidget: Using API data");
        result = apiData;
        usingSampleData = false;
      } else {
        // API data exists but is empty - try calculating from clients
        console.log("⚠️ FEGLIRiskWidget: API data is empty (all zeros), trying client calculation");
        if (clients.length > 0) {
          try {
            // Debug: Check how many clients have FEGLI coverage
            const clientsWithFEGLI = clients.filter(c => 
              c.fegli_basicCoverage || c.fegli_optionA || c.fegli_optionB || c.fegli_optionC
            );
            console.log("🔍 FEGLIRiskWidget: Clients with FEGLI coverage:", clientsWithFEGLI.length, "out of", clients.length);
            
            // Debug: Check how many have reduction percentage
            const clientsWithReduction = clients.filter(c => 
              (c as any).fegliReductionPercentage !== undefined && (c as any).fegliReductionPercentage !== null
            );
            console.log("🔍 FEGLIRiskWidget: Clients with fegliReductionPercentage:", clientsWithReduction.length);
            
            // Debug: Test calculation on sample clients
            if (clientsWithFEGLI.length > 0) {
              const sampleClient = clientsWithFEGLI[0];
              const testReduction = calculateFEGLIRisk([sampleClient]);
              console.log("🔍 FEGLIRiskWidget: Sample client with FEGLI:", {
                id: sampleClient.id,
                fegli_basicCoverage: sampleClient.fegli_basicCoverage,
                fegli_optionA: sampleClient.fegli_optionA,
                fegli_optionB: sampleClient.fegli_optionB,
                fegli_optionC: sampleClient.fegli_optionC,
                fegli_optionBAge65Reduction: (sampleClient as any).fegli_optionBAge65Reduction,
                fegli_optionCAge65Reduction: (sampleClient as any).fegli_optionCAge65Reduction,
                fegli_reductionAtAge65: (sampleClient as any).fegli_reductionAtAge65,
                fegliReductionPercentage: (sampleClient as any).fegliReductionPercentage,
                calculatedReduction: testReduction,
              });
            }
            
            const calculatedData = calculateFEGLIRisk(clients);
            const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
            console.log("🔍 FEGLIRiskWidget: Calculated from clients - Total:", calculatedTotal);
            console.log("🔍 FEGLIRiskWidget: Calculated categories:", JSON.stringify(calculatedData.categories, null, 2));
            
            // Debug: Check what calculateFEGLIReduction returns for each client with FEGLI
            if (clientsWithFEGLI.length > 0 && calculatedTotal === 0) {
              console.warn("⚠️ FEGLIRiskWidget: Found clients with FEGLI but calculation returned 0!");
              console.warn("🔍 FEGLIRiskWidget: Testing calculateFEGLIReduction on each client:");
              clientsWithFEGLI.forEach((c, idx) => {
                const reduction = calculateFEGLIReduction(c);
                console.warn(`  Client ${idx + 1} (ID: ${c.id}):`, {
                  reduction,
                  reductionType: reduction === null ? 'null' : typeof reduction,
                  hasBasic: c.fegli_basicCoverage,
                  hasOptionA: c.fegli_optionA,
                  hasOptionB: c.fegli_optionB,
                  hasOptionC: c.fegli_optionC,
                  optionBReduction: (c as any).fegli_optionBAge65Reduction,
                  optionCReduction: (c as any).fegli_optionCAge65Reduction,
                  generalReduction: (c as any).fegli_reductionAtAge65,
                });
              });
            }
            
            if (calculatedTotal > 0) {
              console.log("✅ FEGLIRiskWidget: Using calculated data from clients");
              result = calculatedData;
              usingSampleData = false;
            } else {
              console.log("⚠️ FEGLIRiskWidget: Calculated data also empty");
              result = {
                categories: {
                  low: 0,
                  moderate: 0,
                  high: 0,
                },
              };
              usingSampleData = false;
            }
          } catch (error) {
            console.error("❌ FEGLIRiskWidget: Error calculating from clients:", error);
            result = {
              categories: {
                low: 0,
                moderate: 0,
                high: 0,
              },
            };
            usingSampleData = false;
          }
        } else {
          console.log("⚠️ FEGLIRiskWidget: No clients available, returning empty structure");
          result = {
            categories: {
              low: 0,
              moderate: 0,
              high: 0,
            },
          };
          usingSampleData = false;
        }
      }
    } else {
      // No API data structure available - try calculating from clients first
      console.log("⚠️ FEGLIRiskWidget: No API data structure available");
      console.log("🔍 FEGLIRiskWidget: apiData:", apiData);
      
      if (clients.length > 0) {
        try {
          // Debug: Check how many clients have FEGLI coverage
          const clientsWithFEGLI = clients.filter(c => 
            c.fegli_basicCoverage || c.fegli_optionA || c.fegli_optionB || c.fegli_optionC
          );
          console.log("🔍 FEGLIRiskWidget: Clients with FEGLI coverage:", clientsWithFEGLI.length, "out of", clients.length);
          
          // Debug: Check how many have reduction percentage
          const clientsWithReduction = clients.filter(c => 
            (c as any).fegliReductionPercentage !== undefined && (c as any).fegliReductionPercentage !== null
          );
          console.log("🔍 FEGLIRiskWidget: Clients with fegliReductionPercentage:", clientsWithReduction.length);
          
          if (clientsWithFEGLI.length > 0) {
            console.log("🔍 FEGLIRiskWidget: Sample client with FEGLI:", {
              id: clientsWithFEGLI[0].id,
              fegli_basicCoverage: clientsWithFEGLI[0].fegli_basicCoverage,
              fegli_optionA: clientsWithFEGLI[0].fegli_optionA,
              fegli_optionB: clientsWithFEGLI[0].fegli_optionB,
              fegli_optionC: clientsWithFEGLI[0].fegli_optionC,
              fegliReductionPercentage: (clientsWithFEGLI[0] as any).fegliReductionPercentage,
            });
          }
          
          // Test calculation on a few sample clients first
          if (clientsWithFEGLI.length > 0) {
            const testClients = clientsWithFEGLI.slice(0, 3);
            console.log("🔍 FEGLIRiskWidget: Testing calculation on sample clients:");
            testClients.forEach((testClient, idx) => {
              const testReduction = calculateFEGLIReduction(testClient);
              console.log(`🔍 FEGLIRiskWidget: Test client ${idx + 1}:`, {
                id: testClient.id,
                hasFEGLI: true,
                fegli_basicCoverage: testClient.fegli_basicCoverage,
                fegli_optionA: testClient.fegli_optionA,
                fegli_optionB: testClient.fegli_optionB,
                fegli_optionC: testClient.fegli_optionC,
                calculatedReduction: testReduction,
                reductionType: testReduction === null ? 'null' : testReduction === 0 ? 'zero' : 'value',
              });
            });
          }
          
          const calculatedData = calculateFEGLIRisk(clients);
          const calculatedTotal = Object.values(calculatedData.categories).reduce((a, b) => a + b, 0);
          console.log("🔍 FEGLIRiskWidget: Calculated from clients - Total:", calculatedTotal);
          console.log("🔍 FEGLIRiskWidget: Calculated categories:", JSON.stringify(calculatedData.categories, null, 2));
          
          // Debug: Check what calculateFEGLIReduction returns for each client with FEGLI
          if (clientsWithFEGLI.length > 0 && calculatedTotal === 0) {
            console.warn("⚠️ FEGLIRiskWidget: WARNING - Found clients with FEGLI coverage but calculation returned 0 total!");
            console.warn("🔍 FEGLIRiskWidget: Testing calculateFEGLIReduction on each client:");
            clientsWithFEGLI.forEach((c, idx) => {
              const reduction = calculateFEGLIReduction(c);
              console.warn(`  Client ${idx + 1} (ID: ${c.id}):`, {
                reduction,
                reductionType: reduction === null ? 'null' : typeof reduction,
                hasBasic: c.fegli_basicCoverage,
                hasOptionA: c.fegli_optionA,
                hasOptionB: c.fegli_optionB,
                hasOptionC: c.fegli_optionC,
                optionBReduction: (c as any).fegli_optionBAge65Reduction,
                optionCReduction: (c as any).fegli_optionCAge65Reduction,
                generalReduction: (c as any).fegli_reductionAtAge65,
              });
            });
          }
          
          if (calculatedTotal > 0) {
            console.log("✅ FEGLIRiskWidget: Using calculated data from clients");
            result = calculatedData;
            usingSampleData = false;
          } else {
            console.log("⚠️ FEGLIRiskWidget: Calculated data empty");
            result = {
              categories: {
                low: 0,
                moderate: 0,
                high: 0,
              },
            };
            usingSampleData = false;
          }
        } catch (error) {
          console.error("❌ FEGLIRiskWidget: Error calculating from clients:", error);
          result = {
            categories: {
              low: 0,
              moderate: 0,
              high: 0,
            },
          };
          usingSampleData = false;
        }
      } else {
        console.log("⚠️ FEGLIRiskWidget: No clients available, returning empty structure");
        result = {
          categories: {
            low: 0,
            moderate: 0,
            high: 0,
          },
        };
        usingSampleData = false;
      }
    }
    
    // Transform to chart data format
    const totalClients = Object.values(result.categories).reduce((a: number, b: number) => a + b, 0);
    console.log("🔍 FEGLIRiskWidget: Total clients:", totalClients);
    console.log("🔍 FEGLIRiskWidget: Category entries:", Object.entries(result.categories));
    
    const chartData = Object.entries(result.categories)
      .filter(([_, count]) => count > 0)
      .map(([key, count]) => ({
        risk: RISK_LABELS[key as keyof typeof RISK_LABELS],
        count,
        percentage: totalClients > 0 ? (count / totalClients) * 100 : 0,
        fill: RISK_COLORS[key as keyof typeof RISK_COLORS],
        key,
      }));
    
    console.log("🔍 FEGLIRiskWidget: Chart data after filtering:", chartData);
    console.log("🔍 FEGLIRiskWidget: Chart data length:", chartData.length);

    return { chartData, totalClients, result, usingSampleData };
  }, [apiData, clients]);

  const isLoadingData = isLoading || apiLoading;

  if (isLoadingData) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.totalClients === 0 || data.chartData.length === 0) {
    return (
      <Card>
        <CardContent>
          <div className="h-[340px] flex items-center justify-center">
            <p className="text-muted-foreground">
              No clients with FEGLI coverage and reduction data available.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardContent>
        {data.usingSampleData && (
          <div className="mb-2 flex justify-end">
            <Badge variant="secondary" className="text-xs">Sample Data</Badge>
          </div>
        )}
        <div className="h-[340px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.chartData} margin={BAR_CHART_MARGINS}>
              <CartesianGrid {...GRID_STYLE} vertical={false} />
              <XAxis 
                dataKey="risk" 
                angle={-45}
                textAnchor="end"
                height={70}
                interval={0}
                tick={AXIS_STYLE.tick}
                tickLine={false}
              />
              <YAxis 
                label={{ 
                  value: "Number of Clients", 
                  angle: -90, 
                  position: "insideLeft",
                  style: AXIS_STYLE.label
                }}
                allowDecimals={false}
                tick={AXIS_STYLE.tick}
                tickLine={false}
                width={25}
              />
              <Tooltip 
                content={<ChartTooltip 
                  formatter={(value, name) => {
                    const dataPoint = data.chartData.find(d => d.count === value);
                    return [
                      `${value} clients (${dataPoint?.percentage.toFixed(1)}%)`,
                      dataPoint?.risk || name
                    ];
                  }}
                />} 
                cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
              />
              <Bar 
                dataKey="count" 
                radius={[8, 8, 0, 0]}
                {...CHART_ANIMATION}
              >
                {data.chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 pt-3 border-t text-center">
          {data.chartData.map((item) => (
            <div key={item.key} className="space-y-1">
              <div className="text-lg font-bold" style={{ color: item.fill }}>
                {item.count}
              </div>
              <div className="text-xs font-medium text-foreground">{item.risk}</div>
              <div className="text-xs text-muted-foreground">
                {item.percentage.toFixed(1)}%
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

